/**
 * Tab-attention and completion-notification helpers for the ~60s generation wait.
 *
 * Layered signals for users who tab away:
 * 1. document.title change (works everywhere, no permission needed — primary signal;
 *    Safari ignores dynamic favicons, so the title must stand alone).
 * 2. Favicon badge swap (progressive enhancement; the three original icon links in
 *    index.html are removed and restored rather than mutated, since they carry
 *    prefers-color-scheme media attributes).
 * 3. Web Notification — only if the user granted permission via the explicit
 *    "Notify me" soft-ask during the wait. Never requested on page load.
 */

const READY_TITLE = "Model ready — Instant 3D AI";
const FAILED_TITLE = "Generation failed — Instant 3D AI";

let originalTitle: string | null = null;
let removedIconLinks: HTMLLinkElement[] = [];
let injectedIconLink: HTMLLinkElement | null = null;
let visibilityListener: (() => void) | null = null;

const badgeFavicon = (color: string): string =>
  `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><circle cx="16" cy="16" r="14" fill="${color}"/></svg>`,
  )}`;

/**
 * Flag the tab for attention when a generation finishes while the tab is hidden.
 * No-op when the tab is visible. Automatically restores itself when the user returns.
 */
export function flagTabAttention(kind: "success" | "error"): void {
  if (typeof document === "undefined" || !document.hidden) return;
  clearTabAttention();
  originalTitle = document.title;
  document.title = kind === "success" ? READY_TITLE : FAILED_TITLE;
  try {
    removedIconLinks = Array.from(
      document.head.querySelectorAll<HTMLLinkElement>('link[rel="icon"]'),
    );
    removedIconLinks.forEach((link) => link.remove());
    injectedIconLink = document.createElement("link");
    injectedIconLink.rel = "icon";
    injectedIconLink.href = badgeFavicon(
      kind === "success" ? "#16a34a" : "#dc2626",
    );
    document.head.appendChild(injectedIconLink);
  } catch {
    // Favicon swap is progressive enhancement; the title change is the primary signal.
  }
  visibilityListener = () => {
    if (!document.hidden) clearTabAttention();
  };
  document.addEventListener("visibilitychange", visibilityListener);
}

/** Restore the original title and favicon links. Safe to call repeatedly. */
export function clearTabAttention(): void {
  if (typeof document === "undefined") return;
  if (visibilityListener) {
    document.removeEventListener("visibilitychange", visibilityListener);
    visibilityListener = null;
  }
  if (originalTitle !== null) {
    document.title = originalTitle;
    originalTitle = null;
  }
  try {
    if (injectedIconLink) {
      injectedIconLink.remove();
      injectedIconLink = null;
    }
    removedIconLinks.forEach((link) => document.head.appendChild(link));
  } catch {
    // Ignore — favicon restoration is best-effort.
  }
  removedIconLinks = [];
}

export type NotifyPermissionState = "unsupported" | NotificationPermission;

export function getNotifyState(): NotifyPermissionState {
  if (typeof window === "undefined" || !("Notification" in window))
    return "unsupported";
  return Notification.permission;
}

/**
 * Request notification permission. Must only be called from an explicit user
 * gesture (the "Notify me" button) — never on load.
 */
export async function requestNotifyPermission(): Promise<NotifyPermissionState> {
  if (typeof window === "undefined" || !("Notification" in window))
    return "unsupported";
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
}

/** Fire a completion notification — only if permitted AND the tab is hidden. */
export function showCompletionNotification(success: boolean): void {
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (Notification.permission !== "granted" || !document.hidden) return;
  try {
    const notification = new Notification(
      success ? "Your 3D model is ready" : "Generation failed",
      {
        body: success
          ? "Come back to view, orbit, and download your model."
          : "Your images are still loaded — you can retry with one click.",
      },
    );
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  } catch {
    // Notification construction can throw on some platforms — non-fatal.
  }
}
